import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mic, Square, Pause, Play, Headphones, ChevronLeft, Sparkles, Crown,
  RotateCcw, Download, Share2, Save, Volume2, AudioWaveform, Loader2, Users,
} from "lucide-react";
import api, { API, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Recorder, MixPlayer, renderMix, defaultMix, EQ_LABELS, MIX_PRESETS, REVERB_PRESETS, EQ_PRESETS } from "@/lib/audioEngine";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import Logo from "@/components/Logo";

function LiveBars({ getData }) {
  const [bars, setBars] = useState(new Array(48).fill(0));
  useEffect(() => {
    let raf;
    const loop = () => { setBars(getData()); raf = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [getData]);
  return (
    <div className="flex items-center justify-center gap-[3px] h-24">
      {bars.map((b, i) => (
        <div key={i} className="w-1.5 rounded-full bg-gradient-to-t from-neon-pink to-neon-cyan" style={{ height: `${Math.max(4, b * 100)}%` }} />
      ))}
    </div>
  );
}

function Meter({ value }) {
  return (
    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-neon-pink transition-[width] duration-75" style={{ width: `${Math.min(100, value * 100)}%` }} />
    </div>
  );
}

export default function Studio() {
  const { songId } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [song, setSong] = useState(null);
  const [phase, setPhase] = useState("prep"); // prep | countdown | recording | mixer | rendering | done
  const [count, setCount] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [devices, setDevices] = useState([]);
  const [monitoring, setMonitoring] = useState(false);
  const [recId, setRecId] = useState(null);
  const [vocalBlob, setVocalBlob] = useState(null);
  const [mix, setMix] = useState(defaultMix());
  const [mode, setMode] = useState("basic");
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [finalRec, setFinalRec] = useState(null);
  const [privacy, setPrivacy] = useState("private");
  const [duetOpen, setDuetOpen] = useState(false);
  const [lyricIdx, setLyricIdx] = useState(0);

  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const mixPlayerRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/songs/${songId}`).then(({ data }) => setSong(data)).catch(() => { toast.error("Song not available."); nav("/record"); });
    return () => { recorderRef.current?.destroy(); mixPlayerRef.current?.destroy(); clearInterval(timerRef.current); };
  }, [songId, nav]);

  useEffect(() => {
    const iv = setInterval(() => { if (recorderRef.current) setLevel(recorderRef.current.getLevel()); }, 80);
    return () => clearInterval(iv);
  }, []);

  const getWave = useCallback(() => recorderRef.current?.getWaveform(48) || new Array(48).fill(0), []);

  const beginSession = async () => {
    try {
      recorderRef.current = new Recorder();
      const devs = await recorderRef.current.init(monitoring);
      setDevices(devs);
    } catch {
      toast.error("Microphone access is required to record. Please allow permission and try again.");
      return;
    }
    try {
      const { data } = await api.post("/recordings/start", { song_id: songId });
      setRecId(data.recording_id);
      await refresh();
    } catch (e) {
      recorderRef.current?.destroy();
      if (e?.response?.status === 402) { setPhase("limit"); return; }
      toast.error(apiErr(e));
      return;
    }
    // countdown
    setPhase("countdown");
    let c = 3; setCount(3);
    const cd = setInterval(() => {
      c -= 1;
      if (c <= 0) { clearInterval(cd); startRecording(); } else setCount(c);
    }, 1000);
  };

  const startRecording = () => {
    setPhase("recording");
    recorderRef.current.start();
    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
    setElapsed(0);
    timerRef.current = setInterval(() => {
      const t = audioRef.current?.currentTime || 0;
      setElapsed(t);
      if (song?.lyrics?.length) {
        const idx = song.lyrics.reduce((acc, l, i) => (t >= l.t ? i : acc), 0);
        setLyricIdx(idx);
      }
    }, 200);
  };

  const stopRecording = async () => {
    clearInterval(timerRef.current);
    audioRef.current?.pause();
    const blob = await recorderRef.current.stop();
    recorderRef.current.setMonitoring(false);
    setVocalBlob(blob);
    mixPlayerRef.current = new MixPlayer();
    await mixPlayerRef.current.load(blob, `${API}/songs/${songId}/track`);
    setPhase("mixer");
  };

  const togglePreview = () => {
    const mp = mixPlayerRef.current;
    if (!mp) return;
    if (previewPlaying) { mp.stop(); setPreviewPlaying(false); }
    else { mp.play(mix, () => setPreviewPlaying(false)); setPreviewPlaying(true); }
  };

  const applyMixPreset = (name) => {
    mixPlayerRef.current?.stop(); setPreviewPlaying(false);
    setMix({ ...mix, ...MIX_PRESETS[name], preset: name });
  };
  const applyReverb = (name) => setMix({ ...mix, reverb: { ...REVERB_PRESETS[name] } });
  const applyEq = (name) => setMix({ ...mix, eq: [...EQ_PRESETS[name]] });

  const doRender = async () => {
    mixPlayerRef.current?.stop(); setPreviewPlaying(false);
    setPhase("rendering"); setProgress(5);
    try {
      const { blob, duration } = await renderMix(vocalBlob, `${API}/songs/${songId}/track`, mix, setProgress);
      const fd = new FormData();
      fd.append("audio", blob, "mix.wav");
      fd.append("duration", String(Math.round(duration)));
      fd.append("mix_settings", JSON.stringify(mix));
      fd.append("status", "completed");
      const { data } = await api.post(`/recordings/${recId}/save`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFinalRec(data);
      setPhase("done");
      toast.success("Your performance is ready!");
    } catch (e) {
      setPhase("mixer");
      toast.error("We couldn't finish your recording. Your original vocal is safe. Please try again.");
    }
  };

  const publish = async (p) => {
    setPrivacy(p);
    try { await api.patch(`/recordings/${recId}`, { privacy: p }); toast.success(p === "public" ? "Published to your profile!" : "Privacy updated."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleDuet = async () => {
    const next = !duetOpen;
    setDuetOpen(next);
    try {
      const { data } = await api.patch(`/recordings/${recId}`, { duet_open: next });
      if (next && data.privacy) setPrivacy(data.privacy);
      toast.success(next ? "Anyone with the link can now duet with you!" : "Duets turned off.");
    } catch (e) { setDuetOpen(!next); toast.error(apiErr(e)); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/recordings/${recId}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${song.title} - SINGVI.wav`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download not available for this recording."); }
  };

  const share = () => {
    const url = `${window.location.origin}/performance/${recId}`;
    if (navigator.share) navigator.share({ title: `My ${song.title} performance`, url }).catch(() => {});
    else { navigator.clipboard.writeText(url); toast.success("Share link copied!"); }
  };

  if (!song) return <div className="min-h-screen bg-mesh" />;
  const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-mesh">
      <div className="glass border-b border-white/10 px-4 h-16 flex items-center justify-between sticky top-0 z-40">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white" data-testid="studio-back"><ChevronLeft className="w-5 h-5" /> Exit</button>
        <Logo size={34} />
        <div className="w-16" />
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Song header */}
        <div className="flex items-center gap-4 mb-8">
          <img src={song.cover_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
          <div><h1 className="font-head text-xl font-bold">{song.title}</h1><p className="text-sm text-muted-foreground">{song.artist}</p></div>
        </div>

        <audio ref={audioRef} src={`${API}/songs/${songId}/track`} onEnded={() => phase === "recording" && stopRecording()} onError={() => { if (phase === "recording") toast.message("Backing track couldn't load — recording your vocals only."); }} />

        {/* PREP */}
        {phase === "prep" && (
          <div className="space-y-6 fade-up">
            <div className="glass rounded-2xl p-5 flex items-start gap-3 border-neon-cyan/30">
              <Headphones className="w-6 h-6 text-neon-cyan flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">For best recording quality, use headphones, AirPods or earphones to prevent karaoke music from leaking into your microphone.</p>
            </div>
            {devices.length > 0 && (
              <div className="card-surface rounded-2xl p-5">
                <p className="text-sm font-semibold mb-2">Detected audio inputs</p>
                <ul className="text-sm text-muted-foreground space-y-1">{devices.slice(0, 4).map((d, i) => <li key={i}>• {d.label || `Microphone ${i + 1}`}</li>)}</ul>
              </div>
            )}
            <div className="card-surface rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold flex items-center gap-2"><Volume2 className="w-4 h-4" /> Input level</span>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input type="checkbox" checked={monitoring} onChange={(e) => { setMonitoring(e.target.checked); recorderRef.current?.setMonitoring(e.target.checked); }} /> Monitor my voice
                </label>
              </div>
              <Meter value={level} />
              <p className="text-xs text-muted-foreground mt-2">Speak or sing to test your mic. We'll capture audio only after you start recording.</p>
            </div>
            <Button onClick={beginSession} data-testid="studio-start" className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold py-7 text-base"><Mic className="w-5 h-5 mr-2" /> Allow mic & Start recording</Button>
          </div>
        )}

        {phase === "limit" && (
          <div className="glass rounded-3xl p-10 text-center fade-up">
            <Crown className="w-12 h-12 text-neon-pink mx-auto mb-4" />
            <h2 className="font-head text-2xl font-extrabold">You've used your 2 free recordings.</h2>
            <p className="text-muted-foreground mt-2 mb-6">Upgrade to Premium to keep singing without limits.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => nav("/premium")} className="rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold px-8" data-testid="limit-go-premium">Go Premium</Button>
              <Button variant="ghost" onClick={() => nav("/")} className="rounded-full">Maybe Later</Button>
            </div>
          </div>
        )}

        {/* COUNTDOWN */}
        {phase === "countdown" && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-8xl font-head font-black text-gradient animate-pulse">{count}</div>
            <p className="text-muted-foreground mt-4">Get ready to sing...</p>
          </div>
        )}

        {/* RECORDING */}
        {phase === "recording" && (
          <div className="space-y-6 fade-up">
            <div className="flex items-center justify-center gap-2 text-neon-pink font-bold">
              <span className="w-3 h-3 rounded-full bg-neon-pink animate-pulse" /> RECORDING · {fmt(elapsed)}
            </div>
            <div className="card-surface rounded-2xl p-6"><LiveBars getData={getWave} /></div>
            <div><p className="text-xs text-muted-foreground mb-1">Vocal level</p><Meter value={level} /></div>

            {song.lyrics?.length > 0 && (
              <div className="card-surface rounded-2xl p-6 h-40 overflow-hidden flex flex-col justify-center text-center">
                {song.lyrics.map((l, i) => (
                  <p key={i} className={`transition-all duration-300 ${i === lyricIdx ? "text-2xl font-head font-bold text-gradient" : "text-sm text-muted-foreground/50"}`}>{l.line}</p>
                )).slice(Math.max(0, lyricIdx - 1), lyricIdx + 2)}
              </div>
            )}

            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-neon-cyan transition-[width]" style={{ width: `${(elapsed / (song.duration || 1)) * 100}%` }} /></div>

            <Button onClick={stopRecording} data-testid="studio-stop" className="w-full rounded-full bg-white text-black hover:bg-white/90 font-bold py-7"><Square className="w-5 h-5 mr-2 fill-black" /> Stop & Mix</Button>
          </div>
        )}

        {/* MIXER */}
        {phase === "mixer" && (
          <div className="space-y-6 fade-up">
            <div className="flex items-center justify-between">
              <h2 className="font-head text-2xl font-extrabold flex items-center gap-2"><AudioWaveform className="w-6 h-6 text-neon-pink" /> Vocal Mixer</h2>
              <div className="flex gap-1 glass rounded-full p-1">
                <button onClick={() => setMode("basic")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${mode === "basic" ? "bg-neon-pink text-white" : "text-muted-foreground"}`} data-testid="mode-basic">Basic</button>
                <button onClick={() => setMode("advanced")} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${mode === "advanced" ? "bg-neon-pink text-white" : "text-muted-foreground"}`} data-testid="mode-advanced">Advanced Mixer</button>
              </div>
            </div>

            <Button onClick={togglePreview} data-testid="preview-btn" className="w-full rounded-full glass border border-white/20 hover:bg-white/10 font-bold py-6">
              {previewPlaying ? <><Pause className="w-5 h-5 mr-2" /> Stop preview</> : <><Play className="w-5 h-5 mr-2" /> Preview mix</>}
            </Button>

            {/* Presets */}
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Presets</p>
              <div className="flex flex-wrap gap-2">
                {Object.keys(MIX_PRESETS).map((p) => (
                  <button key={p} onClick={() => applyMixPreset(p)} data-testid={`preset-${p}`} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${mix.preset === p ? "bg-neon-pink border-neon-pink" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/40"}`}>{p}</button>
                ))}
              </div>
            </div>

            {/* Volumes (always) */}
            <div className="grid sm:grid-cols-2 gap-4">
              <SliderCtl label="Vocal Volume" value={mix.vocalVolume} min={0} max={2} step={0.05} onChange={(v) => setMix({ ...mix, vocalVolume: v })} fmt={(v) => `${Math.round(v * 100)}%`} testid="vocal-volume" />
              <SliderCtl label="Karaoke Volume" value={mix.musicVolume} min={0} max={2} step={0.05} onChange={(v) => setMix({ ...mix, musicVolume: v })} fmt={(v) => `${Math.round(v * 100)}%`} testid="music-volume" />
            </div>

            {mode === "advanced" && (
              <>
                {/* Reverb */}
                <Panel title="Reverb">
                  <div className="flex flex-wrap gap-2 mb-4">{Object.keys(REVERB_PRESETS).map((p) => <PresetChip key={p} onClick={() => applyReverb(p)}>{p}</PresetChip>)}</div>
                  <SliderCtl label="Amount" value={mix.reverb.amount} min={0} max={1} step={0.02} onChange={(v) => setMix({ ...mix, reverb: { ...mix.reverb, amount: v } })} fmt={(v) => `${Math.round(v * 100)}%`} testid="reverb-amount" />
                  <SliderCtl label="Decay (room size)" value={mix.reverb.decay} min={0.3} max={6} step={0.1} onChange={(v) => setMix({ ...mix, reverb: { ...mix.reverb, decay: v } })} fmt={(v) => `${v.toFixed(1)}s`} />
                </Panel>

                {/* EQ */}
                <Panel title="Equalizer">
                  <div className="flex flex-wrap gap-2 mb-4">{Object.keys(EQ_PRESETS).map((p) => <PresetChip key={p} onClick={() => applyEq(p)}>{p}</PresetChip>)}</div>
                  <div className="grid grid-cols-3 sm:grid-cols-9 gap-2">
                    {EQ_LABELS.map((f, i) => (
                      <div key={f} className="flex flex-col items-center gap-2">
                        <input type="range" min={-12} max={12} step={1} value={mix.eq[i]} onChange={(e) => { const eq = [...mix.eq]; eq[i] = Number(e.target.value); setMix({ ...mix, eq }); }}
                          className="h-24 accent-neon-pink" style={{ writingMode: "vertical-lr", direction: "rtl" }} data-testid={`eq-${f}`} />
                        <span className="text-[10px] text-muted-foreground">{f >= 1000 ? `${f / 1000}k` : f}</span>
                        <span className="text-[10px] text-neon-cyan">{mix.eq[i] > 0 ? "+" : ""}{mix.eq[i]}</span>
                      </div>
                    ))}
                  </div>
                </Panel>

                {/* Delay */}
                <Panel title="Delay / Echo">
                  <SliderCtl label="Time" value={mix.delay.time} min={0.05} max={1} step={0.01} onChange={(v) => setMix({ ...mix, delay: { ...mix.delay, time: v } })} fmt={(v) => `${Math.round(v * 1000)}ms`} />
                  <SliderCtl label="Feedback" value={mix.delay.feedback} min={0} max={0.8} step={0.02} onChange={(v) => setMix({ ...mix, delay: { ...mix.delay, feedback: v } })} fmt={(v) => `${Math.round(v * 100)}%`} />
                  <SliderCtl label="Mix" value={mix.delay.mix} min={0} max={1} step={0.02} onChange={(v) => setMix({ ...mix, delay: { ...mix.delay, mix: v } })} fmt={(v) => `${Math.round(v * 100)}%`} testid="delay-mix" />
                </Panel>

                {/* Compression */}
                <Panel title="Compression">
                  <SliderCtl label="Threshold" value={mix.comp.threshold} min={-60} max={0} step={1} onChange={(v) => setMix({ ...mix, comp: { ...mix.comp, threshold: v } })} fmt={(v) => `${v} dB`} />
                  <SliderCtl label="Ratio" value={mix.comp.ratio} min={1} max={20} step={0.5} onChange={(v) => setMix({ ...mix, comp: { ...mix.comp, ratio: v } })} fmt={(v) => `${v}:1`} />
                  <SliderCtl label="Makeup gain" value={mix.comp.makeup} min={0} max={12} step={0.5} onChange={(v) => setMix({ ...mix, comp: { ...mix.comp, makeup: v } })} fmt={(v) => `+${v} dB`} />
                </Panel>
              </>
            )}

            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setMix(defaultMix())} className="rounded-full" data-testid="reset-effects"><RotateCcw className="w-4 h-4 mr-2" /> Reset Effects</Button>
              <Button onClick={doRender} data-testid="render-btn" className="flex-1 rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold py-6">Render Final Track</Button>
            </div>
          </div>
        )}

        {/* RENDERING */}
        {phase === "rendering" && (
          <div className="flex flex-col items-center justify-center py-20 fade-up">
            <Loader2 className="w-12 h-12 text-neon-pink animate-spin mb-6" />
            <p className="font-head text-xl font-bold">Rendering your performance... {Math.round(progress)}%</p>
            <div className="w-full max-w-sm h-2 rounded-full bg-white/10 overflow-hidden mt-4"><div className="h-full bg-gradient-to-r from-neon-pink to-neon-cyan transition-[width]" style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        {/* DONE */}
        {phase === "done" && finalRec && (
          <div className="space-y-6 fade-up">
            <div className="glass rounded-3xl p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-neon-pink/20 flex items-center justify-center mx-auto mb-4 glow-pink"><Sparkles className="w-8 h-8 text-neon-pink" /></div>
              <h2 className="font-head text-2xl font-extrabold">Your performance is ready!</h2>
              <p className="text-muted-foreground mt-1">{song.title} — {song.artist}</p>
              <audio controls src={`${API}/recordings/${recId}/audio`} className="w-full mt-6" data-testid="final-audio" />
            </div>

            <div className="card-surface rounded-2xl p-5">
              <p className="text-sm font-semibold mb-3">Who can see this?</p>
              <div className="grid grid-cols-3 gap-2">
                {[["public", "Public"], ["unlisted", "Link only"], ["private", "Private"]].map(([v, l]) => (
                  <button key={v} onClick={() => publish(v)} data-testid={`privacy-${v}`} className={`py-2.5 rounded-xl text-sm font-medium border transition-colors ${privacy === v ? "bg-neon-pink border-neon-pink" : "border-white/15 text-muted-foreground hover:text-white"}`}>{l}</button>
                ))}
              </div>
              <button onClick={toggleDuet} data-testid="studio-duet-toggle" className={`mt-3 w-full flex items-center justify-between rounded-xl p-3 border transition-colors ${duetOpen ? "bg-neon-cyan/15 border-neon-cyan/50" : "border-white/15"}`}>
                <span className="flex items-center gap-2 text-sm font-medium"><Users className="w-4 h-4 text-neon-cyan" /> Open for duets</span>
                <span className={`text-xs font-bold ${duetOpen ? "text-neon-cyan" : "text-muted-foreground"}`}>{duetOpen ? "ON" : "OFF"}</span>
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Button onClick={share} variant="outline" className="rounded-full glass border-white/20" data-testid="done-share"><Share2 className="w-4 h-4 mr-2" /> Share</Button>
              {song.download_allowed && <Button onClick={download} variant="outline" className="rounded-full glass border-white/20" data-testid="done-download"><Download className="w-4 h-4 mr-2" /> Download</Button>}
              <Button onClick={() => nav("/recordings")} variant="outline" className="rounded-full glass border-white/20"><Save className="w-4 h-4 mr-2" /> My Recordings</Button>
              <Button onClick={() => nav("/record")} className="rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold"><Mic className="w-4 h-4 mr-2" /> Sing again</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SliderCtl({ label, value, min, max, step, onChange, fmt, testid }) {
  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">{label}</span><span className="text-neon-cyan font-mono">{fmt ? fmt(value) : value}</span></div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testid} />
    </div>
  );
}
function Panel({ title, children }) {
  return <div className="card-surface rounded-2xl p-5"><h3 className="font-head font-bold mb-4">{title}</h3>{children}</div>;
}
function PresetChip({ children, ...p }) {
  return <button {...p} className="px-3 py-1.5 rounded-full text-xs font-medium border border-white/15 text-muted-foreground hover:text-white hover:border-neon-cyan transition-colors">{children}</button>;
}
